import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useTotalExpenditure,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Cost Efficiency - Total Expenditure',
  content: [
    {
      title: 'Total expenditure / length ($1,000 / km)',
      description:
  <Text fontSize="inherit">
    This result has been calculated by Road Efficiency Group Te Ringa Maimoa using the total co-funded transport expenditure (excluding Emergency Works) per kilometre of the road network (below). Reported in thousand dollars per kilometre.
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools</Link>
  </Text>,
    },
  ],
};

function TransportProgrammeDeliveryCostEfficiencyTotalExpenditure() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: totalExpenditureData, isLoading: isLoadingExpenditure } = useTotalExpenditure();
  const isLoading = isLoadingExpenditure;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  // Total Expenditure Chart
  const totalExpenditureYears = [...new Set(totalExpenditureData?.map((item) => item.Year))];
  const totalExpenditureChartData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: totalExpenditureData?.map((item) => ({ x: item.Year, y: item.Ratio1000km })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: totalExpenditureData?.map((item) => ({ x: item.Year, y: item.Ratio1000kmPG })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: totalExpenditureData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const totalExpenditureChartSeries = filterChartDataByName(
    totalExpenditureChartData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Cost Efficiency - Total Expenditure"
      informationSource={informationSource}
      isLoading={isLoading || !totalExpenditureData}
      lockYear={totalExpenditureData?.[0]?.LockYear}
    >
      <LineRangeAreaChart
        title={['Total expenditure/length', '($1,000/km)']}
        series={totalExpenditureChartSeries}
        type="rangeArea"
        width="280"
        height="280"
        categories={totalExpenditureYears}
        markers={markers}
        stroke={stroke}
        prefix="$"
        suffix=""
        decimalPoints={1}
        yAxisDecimalPoints={0}
      />
    </ReportSection>
  );
}

export default TransportProgrammeDeliveryCostEfficiencyTotalExpenditure;
